"use client";
// 2026-09-15 FASE 2 FIX 6 (isla interactiva — "use client" SE MANTIENE, no se
// migra a server: el acordeón vive de useState(open) + useRef/scroll-height —
// migrarlo rompería la expansión por teclado; RSC leaves-only = cliente solo
// en esta hoja, paridad Features/Footer).
// 2026-08-23 performance budget — LCP <2.5s, INP <200ms, CLS <0.1, 42% pass only;
// 2026-08-23 FAQ — 5 Q&A, CSS Reveal stagger, WCAG 2.2 keyboard, FAQPage schema on page.tsx;
// 2026-09-05 polish: chevron sin transition-all, radio focus tokenizado — solo clases.

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { localePath, type TranslationKey } from "@/lib/i18n/locales";
import { useLocale, useT } from "@/lib/use-i18n";
import { getFreePlanFaqValues } from "@/lib/plans";
import { AccentTail, Reveal } from "./Reveal";

const FAQS: ReadonlyArray<{ q: TranslationKey; a: TranslationKey }> = [
  { q: "faq1Q", a: "faq1A" },
  { q: "faq2Q", a: "faq2A" },
  { q: "faq3Q", a: "faq3A" },
  { q: "faq4Q", a: "faq4A" },
  { q: "faq5Q", a: "faq5A" },
];
// 2026-10-04 faq2A trae placeholders SSOT ({requests} {tokens} {searches}
// {images} {exam} {exports}) — se interpolan con PLAN_CONFIG.free vía
// getFreePlanFaqValues (mismo helper que el FAQPage schema de page.tsx).

// 2026-09-25 RUN1 FASE5-COMP-C O9b: lee #faq-N de la URL (montaje o
// hashchange). null = sin fragmento válido → default (primer item).
function readOpenFromHash(): number | null {
  if (typeof window === "undefined") return null;
  const match = /^#faq-(\d+)$/.exec(window.location.hash);
  if (!match) return null;
  const index = Number(match[1]);
  return Number.isInteger(index) && index >= 0 && index < FAQS.length
    ? index
    : null;
}

export function FAQ() {
  const t = useT();
  const locale = useLocale();
  const contactHref = localePath(locale, "/contact");
  // 2026-09-25 RUN1 FASE5-COMP-C O9b (SEED=2026-09-25-c6c873bf; research 2026
  // W3C APG accordion
  // https://www.w3.org/WAI/ARIA/apg/patterns/accordion/ — header button +
  // aria-expanded/aria-controls intactos + MDN <a>
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a — fragmentos
  // (#id) como deep-links a secciones: FAQ navegable vía #faq-N — el estado
  // expandido se lee de la URL al montar y cada toggle publica su fragmento
  // con replaceState (sin salto de scroll, sin keys nuevas, sin visual nuevo:
  // solo id + data-testid por item). Por-qué-nueva: antes no había forma de
  // enlazar/compartir una pregunta concreta ni de abrirla desde URL).
  // Lectura del hash en el inicializador (no setState en efecto — lint
  // react-hooks/set-state-in-effect); el efecto solo suscribe hashchange.
  const [open, setOpen] = useState<number | null>(
    () => readOpenFromHash() ?? 0,
  );
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onHashChange = () => {
      const index = readOpenFromHash();
      if (index !== null) setOpen(index);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  // 2026-09-06 a11y: toggle sincrónico (antes getElementById + transitionend
  // frágil: si la transición no disparaba, el estado quedaba colgado). La
  // animación vive en CSS (.accordion-panel grid-rows en globals.css).
  // O9b añade la publicación del hash (replaceState, best-effort) sin tocar
  // la sincronía del toggle.
  const handleToggle = (index: number) => {
    setOpen((prev) => (prev === index ? null : index));
    try {
      if (open === index) {
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        );
      } else {
        window.history.replaceState(null, "", `#faq-${index}`);
      }
    } catch {
      // best-effort (hash opcional, nunca rompe el toggle).
    }
  };

  // APG accordion: Up/Down/Home/End mueven el foco entre cabeceras.
  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    const buttons = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>(
        "[id^='faq-btn-']",
      ) ?? [],
    );
    if (buttons.length === 0) return;
    const current = buttons.indexOf(
      document.activeElement as HTMLButtonElement,
    );
    if (current < 0) return;
    event.preventDefault();
    let next = current;
    if (event.key === "ArrowDown") next = (current + 1) % buttons.length;
    else if (event.key === "ArrowUp")
      next = (current - 1 + buttons.length) % buttons.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = buttons.length - 1;
    buttons[next]?.focus();
  };

  return (
    <section id="faq" className="landing-section relative overflow-hidden">
      <div className="mx-auto grid max-w-container-wide gap-8 gutter-fluid lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)] lg:gap-20">
        <Reveal className="lg:sticky lg:top-28 lg:self-start">
          <h2
            id="faq-heading"
            className="text-section-heading text-expressive-emphasized tracking-[-0.02em] text-balance text-foreground"
          >
            <AccentTail text={t("faqTitle")} />
          </h2>
          <p className="mt-5 max-w-[32ch] text-[14px] leading-[1.7] text-muted-foreground text-pretty">
            {t("faqMissingQuestion")}{" "}
            <Link
              href={contactHref}
              className="rounded-sm font-medium text-foreground underline underline-offset-4 decoration-[var(--border-strong)] hover:decoration-foreground transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            >
              {t("faqContactLink")}
            </Link>
            .
          </p>
        </Reveal>

        {/* 2026-09-04 flat minimal list (Spotify/Linear pattern): hairline dividers, serif questions, chevron — no nested boxes */}
        <Reveal delay={100}>
          <div
            ref={listRef}
            className="border-y border-border/50 divide-y divide-border/50"
            aria-labelledby="faq-heading"
            onKeyDown={handleListKeyDown}
          >
            {FAQS.map((faq, index) => {
              const isOpen = open === index;
              const panelId = `faq-panel-${index}`;
              return (
                <div
                  key={faq.q}
                  id={`faq-${index}`}
                  data-testid={`faq-item-${index}`}
                  className={`faq-item ${isOpen ? "faq-item-open" : ""}`}
                >
                  <button
                    type="button"
                    id={`faq-btn-${index}`}
                    onClick={() => handleToggle(index)}
                    className="group flex w-full items-center gap-4 px-2 py-5 text-left outline-none transition-colors duration-200 motion-reduce:transition-none hover:bg-muted/20 focus-visible:rounded-[var(--radius-lg)] focus-visible:outline-2 focus-visible:outline-[var(--accent)] sm:gap-5 sm:py-6 sm:px-3"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                  >
                    <span
                      aria-hidden="true"
                      className="w-7 shrink-0 text-xs font-normal tabular-nums tracking-[0.08em] text-muted-foreground"
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="faq-question min-w-0 flex-1 text-balance text-foreground font-medium text-[15px] sm:text-base tracking-tight transition-colors motion-reduce:transition-none group-hover:text-foreground">
                      {t(faq.q)}
                    </span>
                    <ChevronDown
                      className={`faq-chevron size-4.5 shrink-0 rounded-full transition-transform duration-200 group-hover:text-foreground group-active:scale-90 motion-reduce:transition-none ${isOpen ? "rotate-180 text-foreground" : "text-muted-foreground/60"}`}
                      aria-hidden="true"
                    />
                  </button>

                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={`faq-btn-${index}`}
                    aria-hidden={!isOpen}
                    className={`accordion-panel motion-reduce:transition-none${isOpen ? " open" : ""}`}
                  >
                    <div>
                      <p className="pb-7 ps-11 pe-2 text-[14.5px] leading-[1.75] tabular-nums text-muted-foreground text-pretty sm:ps-14 sm:pe-4">
                        {t(
                          faq.a,
                          index === 1 ? getFreePlanFaqValues() : undefined,
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
